import { Input, type InputProps } from 'antd';
import type { TextAreaProps } from 'antd/es/input';
import { useId, useMemo, useState } from 'react';
import cn from 'classnames';
import styles from './textarea.module.scss';

type AppTextareaProps = TextAreaProps & {
    label?: string;
    requiredMark?: boolean;
    footnoteMark?: boolean;
};

export const AppTextarea = ({
    label,
    value,
    defaultValue,
    onFocus,
    onBlur,
    required,
    requiredMark,
    footnoteMark,
    className,
    status, // 'error' | 'warning' | undefined
    disabled,
    ...rest
}: AppTextareaProps) => {
    const id = useId();
    const [focused, setFocused] = useState(false);

    const hasValue = useMemo(() => {
        const v = value ?? defaultValue;
        if (v === 0) return true;
        return v !== undefined && String(v ?? '').trim().length > 0;
    }, [value, defaultValue]);

    const isOccupied = focused || hasValue;

    return (
        <div
            className={cn(styles.root, className && className, {
                [styles.isFocused]: focused,
                [styles.hasValue]: hasValue,
                [styles.isDisabled]: disabled,
                [styles.isError]: status === 'error',
                [styles.isWarning]: status === 'warning',
            })}
        >
            <Input.TextArea
                id={rest.id ?? id}
                {...rest}
                value={value}
                defaultValue={defaultValue}
                disabled={disabled}
                status={status as InputProps['status']}
                bordered={false}
                rootClassName={cn(styles.control, (rest as any)?.rootClassName)}
                onFocus={e => {
                    setFocused(true);
                    onFocus?.(e);
                }}
                onBlur={e => {
                    setFocused(false);
                    onBlur?.(e);
                }}
            />

            {label && (
                <label
                    htmlFor={rest.id ?? id}
                    className={cn(
                        styles.label,
                        isOccupied ? styles.asLabel : styles.asPlaceholder
                    )}
                >
                    {label}
                    {(required || requiredMark) && (
                        <span className={styles.requiredMark}>*</span>
                    )}
                    {footnoteMark && (
                        <span className={styles.footnoteMark}>*</span>
                    )}
                </label>
            )}
        </div>
    );
};
